import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  DndContext, closestCenter, KeyboardSensor, MouseSensor, TouchSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, rectSortingStrategy, sortableKeyboardCoordinates, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Siren, Eye, ClipboardList, ListChecks, FileCheck, ShieldAlert, Wrench,
  GraduationCap, ClipboardCheck, Users, HardHat, FileText, BarChart3,
  TrendingUp, UserCheck, Building2, FileBarChart, Scale, HeartPulse, Gauge,
  UsersRound, Search, X, GripVertical, RotateCcw,
} from 'lucide-react';
import TabNavigation from '../components/TabNavigation';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { usePermissions } from '../context/PermissionContext';
import {
  safetyDashboardAccess, canReadAnalytics, canAccessSupervisorDashboard, canAccessWorkforce,
} from '@/dashboard/presets/safety';
// import { safetyDashboardAccess } from '@/dashboard/presets/safety';
import { getLayout, saveLayout, resetLayout } from '@/dashboard/api/client';
import {
  SAFETY_HUB_LAYOUT_KEY, idsFromLayout, applySavedOrder, buildLayoutPayload, moveCard,
} from '../utils/safetyHubOrder';

/**
 * Safety Management hub.
 *
 * Ordered by HOW OFTEN a thing is used, not by how the modules were built.
 * Reporting an incident or an observation happens many times a day and used to
 * sit below ten module tiles, ~1,500px down the page; the modules a site
 * touches monthly were at the top. Everything frequent is now above the fold.
 *
 * The cards are DATA, not markup. Twenty hand-written tiles is why colour
 * stopped meaning anything and why one icon ended up on two different modules.
 */

// Section accents. Colour marks the GROUP, so it carries meaning — a different
// tint per card told the reader nothing except that the palette was large.
const ACCENTS = {
  report: { icon: 'bg-red-50 text-red-600', ring: 'hover:border-red-300' },
  manage: { icon: 'bg-blue-50 text-blue-600', ring: 'hover:border-blue-300' },
  insight: { icon: 'bg-violet-50 text-violet-600', ring: 'hover:border-violet-300' },
  govern: { icon: 'bg-emerald-50 text-emerald-600', ring: 'hover:border-emerald-300' },
};

/**
 * One hub card, draggable within its own section.
 *
 * Mouse: drag anywhere on the card after a few pixels of movement — a plain
 * click still navigates (dnd-kit swallows the click only when a drag actually
 * started). Touch: long-press, so a normal swipe still scrolls the page.
 * Keyboard: focus the grip handle, Space/Enter to pick up, arrows to move,
 * Space/Enter to drop, Escape to cancel. The handle is the activator node, so
 * the keyboard sensor ignores keys pressed on the card button — Enter on the
 * card still opens it.
 *
 * Declared at module level, not inside SafetyHub: a component defined in the
 * render body is a new type every render, which would remount each card (and
 * drop its drag state) on every re-render.
 */
const SortableCard = ({ card, accent, primary, disabled, onOpen }) => {
  const {
    attributes, listeners, setNodeRef, setActivatorNodeRef,
    transform, transition, isDragging,
  } = useSortable({ id: card.key, disabled });
  const Icon = card.icon;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: 'manipulation',
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      className={`relative group ${isDragging ? 'z-10 opacity-80' : ''}`}
    >
      <button
        onClick={() => onOpen(card.route)}
        className={`flex items-start gap-3 w-full h-full text-left bg-white border border-gray-200
                    rounded-xl p-4 transition-all duration-150
                    hover:shadow-md hover:-translate-y-0.5 ${accent.ring}
                    ${isDragging ? 'shadow-lg' : ''}
                    focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500`}
      >
        <span className={`shrink-0 grid place-items-center rounded-lg
                          ${primary ? 'w-11 h-11' : 'w-9 h-9'} ${accent.icon}`}>
          <Icon className={primary ? 'w-5 h-5' : 'w-[18px] h-[18px]'} strokeWidth={2} />
        </span>
        <span className="min-w-0 pr-4">
          <span className={`block font-semibold text-gray-900 ${primary ? 'text-base' : 'text-sm'}`}>
            {card.title}
          </span>
          <span className="block text-xs text-gray-500 mt-0.5 leading-snug">
            {card.desc}
          </span>
        </span>
      </button>
      {!disabled && (
        // A sibling of the card button, not a child: an interactive element
        // nested inside a <button> is invalid markup.
        <span
          ref={setActivatorNodeRef}
          {...attributes}
          aria-label={`Reorder ${card.title}`}
          title="Drag to reorder"
          className="absolute top-2 right-2 p-0.5 rounded text-gray-300 cursor-grab
                     opacity-0 group-hover:opacity-100 focus:opacity-100
                     [@media(hover:none)]:opacity-100 hover:text-gray-500
                     focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <GripVertical className="w-4 h-4" />
        </span>
      )}
    </div>
  );
};

const SafetyHub = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { isModuleEnabled, loading } = useModuleAccess();
  const { hasPermission, getResourcePermissions, isAdmin } = usePermissions();
  const [query, setQuery] = useState('');

  // HIRA-specific access: hasPermission() matches on resource OR module_slug,
  // and hazards/risk_assessments share module_slug 'hira', so it returns true
  // for anyone with hazards:read. Gate the HIRA tile on an actual resource:'hira'
  // permission instead, matching the backend route guard (RequirePermission).
  const canAccessHira = getResourcePermissions('hira').some(p => p.action === 'read');
  // Dashboard tiles follow the same grant the dashboards themselves check,
  // so a tile never leads to a screen that redirects straight back.
  const canSeeDashboards = safetyDashboardAccess(hasPermission, user?.role).analytics;
  // Executive / Supervisor / Workforce tiles call the SAME predicate as their
  // App.js route guards (rules documented in presets/safety.js), with the
  // admin bypass coming from PermissionContext.isAdmin() like the routes'.
  const permCtx = { isAdmin, getResourcePermissions };
  const canSeeExecutive = canReadAnalytics(permCtx);
  const canSeeSupervisor = canAccessSupervisorDashboard(permCtx, user?.role);
  const canSeeWorkforce = isModuleEnabled('safety') && canAccessWorkforce(user?.role);
  const canSeeContractors = hasPermission('contractors', 'read');
  // SSEM: backend router requires ssem:read (resource-level, like the routes).
  const canSeeSsem = isAdmin() || getResourcePermissions('ssem').some(p => p.action === 'read');

  // Every `show` below is the SAME gate the previous markup used, card for
  // card. A visual reorganisation must not quietly widen who sees what.
  //
  // `key` is the card's stable id in the user's saved order (dashboard_key
  // 'safety-hub'). Never rename one: a saved order referring to the old key
  // would silently fall back to the default position for that card.
  const sections = useMemo(() => [
    {
      id: 'report',
      title: 'Report & review',
      hint: 'Day-to-day reporting — the things done most often',
      primary: true,
      cards: [
        { key: 'report-incident', title: 'Report Incident', desc: 'Incidents, injuries and near misses',
          route: '/report', icon: Siren, show: hasPermission('incidents', 'create') },
        { key: 'report-observation', title: 'Report Observation', desc: 'Unsafe acts and unsafe conditions',
          route: '/observations/create', icon: Eye, show: hasPermission('observations', 'create') },
        { key: 'view-incidents', title: 'View Incidents', desc: 'Track and manage reported incidents',
          route: '/incidents', icon: ClipboardList, show: hasPermission('incidents', 'read') },
        { key: 'view-observations', title: 'View Observations', desc: 'Track and manage reported observations',
          route: '/observations', icon: ListChecks, show: hasPermission('observations', 'read') },
      ],
    },
    {
      id: 'manage',
      title: 'Safety modules',
      hint: 'Permits, risk, actions, people and records',
      cards: [
        { key: 'ptw', title: 'Permit to Work', desc: 'Permits for high-risk activities',
          route: '/ptw', icon: FileCheck, show: isModuleEnabled('ptw') },
        { key: 'hira', title: 'HIRA', desc: 'Hazard identification & risk assessment',
          route: '/hira', icon: ShieldAlert, show: isModuleEnabled('hira') && canAccessHira },
        { key: 'capa', title: 'CAPA', desc: 'Corrective & preventive actions',
          route: '/capa', icon: Wrench, show: isModuleEnabled('capa') },
        { key: 'training', title: 'Training', desc: 'Training, certifications & competency',
          route: '/training', icon: GraduationCap, show: isModuleEnabled('training') },
        { key: 'audits', title: 'Audits & Inspections', desc: 'Compliance audits and inspections',
          route: '/audits', icon: ClipboardCheck, show: isModuleEnabled('audits') },
        { key: 'meetings', title: 'Safety Meetings', desc: 'Meetings, toolbox talks & actions',
          route: '/meetings', icon: Users, show: isModuleEnabled('meetings') },
        { key: 'contractors', title: 'Contractors', desc: 'Firms, workers, induction & eligibility',
          route: '/contractors', icon: HardHat, show: canSeeContractors },
        { key: 'documents', title: 'Document Control', desc: 'SOPs, policies & procedures',
          route: '/documents', icon: FileText, show: isModuleEnabled('documents') },
      ],
    },
    {
      id: 'insight',
      title: 'Dashboards & reports',
      hint: 'Performance, trends and the management pack',
      cards: [
        { key: 'safety-dashboard', title: 'Safety Dashboard', desc: 'KPIs, trends, causes and repeat incidents',
          route: '/safety/dashboard', icon: BarChart3, show: canSeeDashboards },
        { key: 'executive-dashboard', title: 'Executive Dashboard', desc: 'Leading indicators, PSS and insights',
          route: '/dashboard/executive', icon: TrendingUp, show: canSeeExecutive },
        { key: 'supervisor-dashboard', title: 'Supervisor Dashboard', desc: 'Mobile-friendly frontline view',
          route: '/dashboard/supervisor', icon: UserCheck, show: canSeeSupervisor },
        { key: 'contractor-dashboard', title: 'Contractor Dashboard', desc: 'Compliance, eligibility and EHS performance',
          route: '/contractors/dashboard', icon: Building2, show: canSeeContractors },
        { key: 'ehs-mis', title: 'EHS MIS & Reports', desc: 'Monthly performance and the management report',
          route: '/safety/ehs-mis', icon: FileBarChart, show: canSeeDashboards },
      ],
    },
    {
      id: 'govern',
      title: 'Governance & records',
      hint: 'Obligations, health surveillance and workforce data',
      cards: [
        { key: 'legal-compliance', title: 'Legal Compliance', desc: 'Statutory requirements, due dates and evidence',
          route: '/safety/compliance-register', icon: Scale, show: canSeeDashboards },
        { key: 'occupational-health', title: 'Occupational Health', desc: 'Surveillance, fitness and illness cases',
          route: '/safety/occupational-health', icon: HeartPulse, show: canSeeDashboards },
        { key: 'ssem', title: 'SSEM', desc: 'Safety excellence maturity assessment',
          route: '/ssem', icon: Gauge, show: isModuleEnabled('ssem') && canSeeSsem },
        // Was gated on the DOCUMENTS module (a copy-paste slip, QA DSH-03). Now
        // the same gate as the /safety/workforce route: safety module + the
        // backend's WORKFORCE_ALLOWED_ROLES (mirrored in presets/safety.js).
        { key: 'workforce-data', title: 'Workforce Data', desc: 'Monthly headcount and manhours per site',
          route: '/safety/workforce', icon: UsersRound, show: canSeeWorkforce },
      ],
    },
  ], [isModuleEnabled, hasPermission, canAccessHira, canSeeDashboards,
      canSeeExecutive, canSeeSupervisor, canSeeWorkforce, canSeeContractors, canSeeSsem]);

  // ---- Per-user card order (drag-and-drop within a section) ----
  // Stored through the shared per-user dashboard layout store, so no new
  // endpoint: [{id: card key}, ...]. An empty list means "default order".
  // Loading never blocks the page — it renders in the default order and
  // re-sorts if a saved order arrives; any load failure keeps the default.
  const [orderIds, setOrderIds] = useState([]);
  const saveSeq = useRef(0);

  useEffect(() => {
    const ctrl = new AbortController();
    getLayout(SAFETY_HUB_LAYOUT_KEY, { signal: ctrl.signal })
      .then((saved) => {
        // A drop or reset made before the load finished wins over the load.
        if (!ctrl.signal.aborted && saveSeq.current === 0) setOrderIds(idsFromLayout(saved));
      })
      .catch(() => { /* default order */ });
    return () => ctrl.abort();
  }, []);

  const ordered = useMemo(() => applySavedOrder(sections, orderIds), [sections, orderIds]);
  const customised = orderIds.length > 0;

  const sensors = useSensors(
    // A few pixels of travel before a drag starts, so a click still navigates.
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    // Long-press on touch, so a normal swipe still scrolls the page.
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Optimistic: the new order shows immediately and is saved in the
  // background. On failure it reverts — unless a later drop has superseded it,
  // in which case that later save owns the outcome.
  const handleDragEnd = useCallback(({ active, over }) => {
    if (!over || active.id === over.id) return;
    const next = moveCard(ordered, active.id, over.id);
    if (next === ordered) return; // dropped outside its own section
    const previous = orderIds;
    const payload = buildLayoutPayload(next);
    setOrderIds(payload.map((e) => e.id));
    const seq = ++saveSeq.current;
    saveLayout(SAFETY_HUB_LAYOUT_KEY, payload).catch(() => {
      if (seq !== saveSeq.current) return;
      setOrderIds(previous);
      toast.error('Could not save the card order. Please try again.');
    });
  }, [ordered, orderIds]);

  const handleResetOrder = useCallback(() => {
    const seq = ++saveSeq.current;
    resetLayout(SAFETY_HUB_LAYOUT_KEY)
      .then(() => { if (seq === saveSeq.current) setOrderIds([]); })
      .catch(() => toast.error('Could not reset the card order. Please try again.'));
  }, []);

  // Filter by name or description. With twenty-odd destinations, typing three
  // letters beats scanning four sections.
  const term = query.trim().toLowerCase();
  // Reordering a filtered subset is ambiguous (where do the hidden matches
  // go?), so dragging is off while a search is active.
  const dragDisabled = !!term;
  const visible = ordered
    .map(s => ({
      ...s,
      cards: s.cards.filter(c => c.show && (
        !term || c.title.toLowerCase().includes(term) || c.desc.toLowerCase().includes(term)
      )),
    }))
    .filter(s => s.cards.length > 0);

  const totalShown = visible.reduce((n, s) => n + s.cards.length, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Safety Management</h1>
            <p className="text-gray-500 text-sm mt-1">
              Reporting, modules, dashboards and records
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-3 w-full sm:w-auto">
            {/* Only offered when there is a saved order to undo. */}
            {customised && (
              <button
                type="button"
                onClick={handleResetOrder}
                title="Put the cards back in their default order"
                className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800
                           focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md px-1"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Reset order
              </button>
            )}

            <div className="relative w-full sm:w-72">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Find a module…"
                className="w-full pl-9 pr-8 py-2 text-sm bg-white border border-gray-300 rounded-lg
                           focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  aria-label="Clear"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-20 bg-white border border-gray-200 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            {visible.map(section => {
              const accent = ACCENTS[section.id];
              return (
                <section key={section.id} className="mb-8">
                  <div className="flex items-baseline gap-2 mb-3">
                    <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                      {section.title}
                    </h2>
                    <span className="text-xs text-gray-400">{section.hint}</span>
                  </div>
                  {/* One DndContext per section: a card can only ever be
                      dropped among its own section's cards. */}
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={section.cards.map(c => c.key)}
                      strategy={rectSortingStrategy}
                    >
                      <div className={`grid gap-4 grid-cols-1 sm:grid-cols-2 ${
                        section.primary ? 'lg:grid-cols-4' : 'lg:grid-cols-4'
                      }`}>
                        {section.cards.map(card => (
                          <SortableCard key={card.key} card={card} accent={accent}
                                        primary={section.primary} disabled={dragDisabled}
                                        onOpen={navigate} />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </section>
              );
            })}

            {totalShown === 0 && (
              <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
                <p className="text-gray-900 font-medium">
                  {term ? `Nothing matches “${query}”` : 'No safety modules are available to you'}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {term
                    ? 'Try a shorter search, or clear it to see everything.'
                    : 'Ask your administrator to enable the modules you need.'}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SafetyHub;
